import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography, Alert, Button, Stack, IconButton } from '@mui/material';
import { NavigateBefore, NavigateNext } from '@mui/icons-material';
import * as pdfjsLib from 'pdfjs-dist';

// Use the CDN worker file with HTTPS
const pdfVersion = pdfjsLib.version;
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfVersion}/pdf.worker.min.js`;

// Initialize PDF.js - important to do this once at the module level
console.log('PDF.js initializing with version:', pdfVersion, 'and worker:', pdfjsLib.GlobalWorkerOptions.workerSrc);

// Check if worker is available
const checkWorker = async () => {
  try {
    // Create a tiny PDF for testing
    const testPdf = new Uint8Array([
      0x25, 0x50, 0x44, 0x46, 0x2d, 0x31, 0x2e, 0x34, 0x0a, 0x25, 0xe2, 0xe3,
      0xcf, 0xd3, 0x0a, 0x31, 0x20, 0x30, 0x20, 0x6f, 0x62, 0x6a, 0x0a, 0x3c,
      0x3c, 0x2f, 0x54, 0x79, 0x70, 0x65, 0x2f, 0x43, 0x61, 0x74, 0x61, 0x6c,
      0x6f, 0x67, 0x2f, 0x50, 0x61, 0x67, 0x65, 0x73, 0x20, 0x32, 0x20, 0x30,
      0x20, 0x52, 0x3e, 0x3e, 0x0a, 0x65, 0x6e, 0x64, 0x6f, 0x62, 0x6a, 0x0a,
      0x32, 0x20, 0x30, 0x20, 0x6f, 0x62, 0x6a, 0x0a, 0x3c, 0x3c, 0x2f, 0x54,
      0x79, 0x70, 0x65, 0x2f, 0x50, 0x61, 0x67, 0x65, 0x73, 0x2f, 0x43, 0x6f,
      0x75, 0x6e, 0x74, 0x20, 0x31, 0x2f, 0x4b, 0x69, 0x64, 0x73, 0x5b, 0x33,
      0x20, 0x30, 0x20, 0x52, 0x5d, 0x3e, 0x3e, 0x0a, 0x65, 0x6e, 0x64, 0x6f,
      0x62, 0x6a, 0x0a, 0x33, 0x20, 0x30, 0x20, 0x6f, 0x62, 0x6a, 0x0a, 0x3c,
      0x3c, 0x2f, 0x54, 0x79, 0x70, 0x65, 0x2f, 0x50, 0x61, 0x67, 0x65, 0x3e,
      0x3e, 0x0a, 0x65, 0x6e, 0x64, 0x6f, 0x62, 0x6a, 0x0a, 0x78, 0x72, 0x65,
      0x66, 0x0a, 0x30, 0x20, 0x34, 0x0a, 0x30, 0x30, 0x30, 0x30, 0x30, 0x30,
      0x30, 0x30, 0x30, 0x30, 0x20, 0x36, 0x35, 0x35, 0x33, 0x35, 0x20, 0x66,
      0x0a, 0x30, 0x30, 0x30, 0x30, 0x30, 0x30, 0x30, 0x30, 0x31, 0x30, 0x20,
      0x30, 0x30, 0x30, 0x30, 0x30, 0x20, 0x6e, 0x0a, 0x30, 0x30, 0x30, 0x30,
      0x30, 0x30, 0x30, 0x30, 0x37, 0x39, 0x20, 0x30, 0x30, 0x30, 0x30, 0x30,
      0x20, 0x6e, 0x0a, 0x30, 0x30, 0x30, 0x30, 0x30, 0x30, 0x30, 0x31, 0x37,
      0x33, 0x20, 0x30, 0x30, 0x30, 0x30, 0x30, 0x20, 0x6e, 0x0a, 0x74, 0x72,
      0x61, 0x69, 0x6c, 0x65, 0x72, 0x0a, 0x3c, 0x3c, 0x2f, 0x53, 0x69, 0x7a,
      0x65, 0x20, 0x34, 0x2f, 0x52, 0x6f, 0x6f, 0x74, 0x20, 0x31, 0x20, 0x30,
      0x20, 0x52, 0x3e, 0x3e, 0x0a, 0x73, 0x74, 0x61, 0x72, 0x74, 0x78, 0x72,
      0x65, 0x66, 0x0a, 0x32, 0x30, 0x39, 0x0a, 0x25, 0x25, 0x45, 0x4f, 0x46
    ]);

    // Try to load the PDF with a short timeout
    const loadingTask = pdfjsLib.getDocument({data: testPdf});
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Worker load timeout')), 3000);
    });
    
    await Promise.race([loadingTask.promise, timeoutPromise]);
    console.log('PDF.js worker successfully loaded');
    return true;
  } catch (error) {
    console.error('PDF.js worker failed to load:', error);
    return false;
  }
};

// Run the check when module loads
checkWorker();

type EditMode = 'text' | 'image' | 'draw' | 'highlight' | 'shape' | null;

interface Annotation {
  id: string;
  type: string;
  pageNumber: number;
  x: number;
  y: number;
  width?: number;
  height?: number;
  color?: string;
  content?: string;
  path?: { x: number; y: number }[];
}

interface PDFViewerProps {
  file: File | null;
  scale?: number;
  editMode?: EditMode;
  onAnnotationAdded?: (annotation: Annotation) => void;
  onAnnotationUpdated?: (annotation: Annotation) => void;
  onAnnotationDeleted?: (id: string) => void;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ 
  file, 
  scale = 1.0, 
  editMode = null,
  onAnnotationAdded,
  onAnnotationUpdated,
  onAnnotationDeleted
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const annotationLayerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);
  const [renderedOnce, setRenderedOnce] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [pdfDocument, setPdfDocument] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [pageRendering, setPageRendering] = useState(false);
  const [pageNumPending, setPageNumPending] = useState<number | null>(null);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState<{x: number, y: number}[]>([]);
  const [viewport, setViewport] = useState<any>(null);

  // Effect to load the PDF document when file changes
  useEffect(() => {
    if (!file) {
      console.log('No file provided to PDFViewer');
      return;
    }

    // Reset state for new file
    setLoading(true);
    setError(null);
    setDebugInfo(null);
    setRenderedOnce(false);
    setPdfDocument(null);
    setCurrentPage(1);
    setNumPages(0);
    setAnnotations([]);

    console.log(`Attempting to load PDF: ${file.name}, size: ${file.size} bytes`);

    const loadPDF = async () => {
      try {
        setDebugInfo('Reading file as ArrayBuffer...');
        const arrayBuffer = await file.arrayBuffer();
        setDebugInfo('File read successfully. Size: ' + arrayBuffer.byteLength + ' bytes');
        
        // Load the PDF document
        setDebugInfo('Creating PDF loading task...');
        const loadingTask = pdfjsLib.getDocument({
          data: arrayBuffer,
          verbosity: pdfjsLib.VerbosityLevel.ERRORS
        });

        setDebugInfo('Waiting for PDF to load...');
        const pdf = await loadingTask.promise;
        setDebugInfo(`PDF loaded successfully. Pages: ${pdf.numPages}`);
        setNumPages(pdf.numPages);
        
        setPdfDocument(pdf);
      } catch (err) {
        console.error('Error loading PDF document:', err);
        setError(err instanceof Error ? err.message : 'Failed to load PDF document');
        setLoading(false);
      }
    };

    loadPDF();
  }, [file]);

  // Function to render a specific page
  const renderPage = async (pageNum: number) => {
    if (!pdfDocument || !canvasRef.current) return;
    
    setPageRendering(true);
    
    try {
      setDebugInfo(`Getting page ${pageNum}...`);
      const page = await pdfDocument.getPage(pageNum);
      setDebugInfo(`Page ${pageNum} retrieved`);
      
      // Make sure canvas is still available
      if (!canvasRef.current) {
        throw new Error('Canvas element became unavailable');
      }
      
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d', { alpha: false });
      if (!context) {
        throw new Error('Could not get canvas context');
      }
      setDebugInfo('Canvas context obtained');

      // Clear previous content
      context.fillStyle = 'white';
      context.fillRect(0, 0, canvas.width, canvas.height);

      // Calculate viewport dimensions with scale
      const newViewport = page.getViewport({ scale });
      setViewport(newViewport);
      
      // Set canvas dimensions to match the viewport
      canvas.height = newViewport.height;
      canvas.width = newViewport.width;
      setDebugInfo(`Viewport set: ${newViewport.width}x${newViewport.height}`);

      // Render the page with high quality
      const renderContext = {
        canvasContext: context,
        viewport: newViewport,
        renderInteractiveForms: true,
        enableWebGL: true
      };

      setDebugInfo(`Rendering page ${pageNum}...`);
      const renderTask = page.render(renderContext);
      
      // Wait for rendering to complete
      await renderTask.promise;

      setDebugInfo(`Page ${pageNum} rendered successfully`);
      console.log(`PDF page ${pageNum} rendered successfully`);
      setRenderedOnce(true);
      setPageRendering(false);
      
      // If another page was requested while this one was rendering, render that one now
      if (pageNumPending !== null) {
        const pendingPage = pageNumPending;
        setPageNumPending(null);
        renderPage(pendingPage);
      }
    } catch (err) {
      console.error('Error rendering PDF page:', err);
      setError(err instanceof Error ? err.message : 'Failed to render PDF page');
      setPageRendering(false);
      setLoading(false);
    }
  };
  
  // Queue a new page render if one is already in progress
  const queueRenderPage = (pageNum: number) => {
    if (pageRendering) {
      setPageNumPending(pageNum);
    } else {
      renderPage(pageNum);
    }
  };

  // Navigate to previous page
  const onPrevPage = () => {
    if (currentPage <= 1) return;
    setCurrentPage(prev => prev - 1);
  };

  // Navigate to next page
  const onNextPage = () => {
    if (currentPage >= numPages) return;
    setCurrentPage(prev => prev + 1);
  };

  // Effect to render the current page when it changes or when document/scale changes
  useEffect(() => {
    if (!pdfDocument) return;
    
    queueRenderPage(currentPage);
    setLoading(false);
  }, [pdfDocument, currentPage, scale]);

  // Handle mouse events for annotations
  const getMousePosition = (event: React.MouseEvent) => {
    if (!canvasRef.current || !viewport) return null;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    return { x, y };
  };
  
  const handleMouseDown = (event: React.MouseEvent) => {
    if (!editMode || !canvasRef.current || !viewport) return;
    
    const position = getMousePosition(event);
    if (!position) return;
    
    if (editMode === 'draw') {
      setIsDrawing(true);
      setCurrentPath([position]);
    } else if (editMode === 'text') {
      // Trigger text annotation dialog in parent component
      if (onAnnotationAdded) {
        // Create a placeholder annotation to be replaced with actual content
        const tempAnnotation: Annotation = {
          id: `text-${Date.now()}`,
          type: 'text',
          pageNumber: currentPage,
          x: position.x,
          y: position.y,
          content: 'Click to edit' // Placeholder
        };
        onAnnotationAdded(tempAnnotation);
      }
    } else if (editMode === 'image') {
      // Trigger image upload in parent component
      if (onAnnotationAdded) {
        // Create a placeholder annotation to be replaced with actual image
        const tempAnnotation: Annotation = {
          id: `image-${Date.now()}`,
          type: 'image',
          pageNumber: currentPage,
          x: position.x,
          y: position.y
        };
        onAnnotationAdded(tempAnnotation);
      }
    } else if (editMode === 'highlight') {
      // Start highlight selection
      setIsDrawing(true);
      setCurrentPath([position]);
    }
  };
  
  const handleMouseMove = (event: React.MouseEvent) => {
    if (!isDrawing || !editMode) return;
    
    const position = getMousePosition(event);
    if (!position) return;
    
    if (editMode === 'draw' || editMode === 'highlight') {
      setCurrentPath(prev => [...prev, position]);
    }
  };
  
  const handleMouseUp = (event: React.MouseEvent) => {
    if (!editMode || !canvasRef.current || !viewport) return;
    
    const position = getMousePosition(event);
    if (!position) return;
    
    if (editMode === 'draw' && isDrawing) {
      setIsDrawing(false);
      
      if (currentPath.length > 1) {
        const newAnnotation: Annotation = {
          id: `drawing-${Date.now()}`,
          type: 'drawing',
          pageNumber: currentPage,
          x: Math.min(...currentPath.map(p => p.x)),
          y: Math.min(...currentPath.map(p => p.y)),
          path: currentPath,
          color: '#FF0000' // Default red
        };
        
        setAnnotations(prev => [...prev, newAnnotation]);
        if (onAnnotationAdded) onAnnotationAdded(newAnnotation);
      }
      
      setCurrentPath([]);
    } else if (editMode === 'highlight' && isDrawing) {
      setIsDrawing(false);
      
      if (currentPath.length > 1) {
        // For highlight, we create a rectangle from the start to end points
        const startX = currentPath[0].x;
        const startY = currentPath[0].y;
        const endX = position.x;
        const endY = position.y;
        
        const x = Math.min(startX, endX);
        const y = Math.min(startY, endY);
        const width = Math.abs(endX - startX);
        const height = Math.abs(endY - startY);
        
        const newAnnotation: Annotation = {
          id: `highlight-${Date.now()}`,
          type: 'highlight',
          pageNumber: currentPage,
          x,
          y,
          width,
          height,
          color: '#FFFF00' // Yellow
        };
        
        setAnnotations(prev => [...prev, newAnnotation]);
        if (onAnnotationAdded) onAnnotationAdded(newAnnotation);
      }
      
      setCurrentPath([]);
    }
  };

  // Render annotations on the annotation layer
  const renderAnnotations = () => {
    const currentAnnotations = annotations.filter(a => a.pageNumber === currentPage);
    
    return currentAnnotations.map(annotation => {
      switch (annotation.type) {
        case 'drawing':
          return (
            <svg 
              key={annotation.id}
              style={{ 
                position: 'absolute', 
                top: 0, 
                left: 0,
                width: viewport?.width || '100%',
                height: viewport?.height || '100%',
                pointerEvents: 'none'
              }}
            >
              <polyline
                points={annotation.path?.map(p => `${p.x},${p.y}`).join(' ')}
                fill="none"
                stroke={annotation.color || '#FF0000'}
                strokeWidth="2"
              />
            </svg>
          );
          
        case 'text':
          return (
            <div 
              key={annotation.id}
              style={{ 
                position: 'absolute',
                left: annotation.x,
                top: annotation.y,
                backgroundColor: 'white',
                border: '1px solid black',
                padding: 5,
                minWidth: 100,
                maxWidth: 200,
                borderRadius: 3,
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                cursor: 'pointer',
                zIndex: 10
              }}
              onClick={() => {
                if (onAnnotationUpdated && annotation.id) {
                  onAnnotationUpdated(annotation);
                }
              }}
            >
              {annotation.content || 'Text note'}
              <div 
                style={{ 
                  position: 'absolute', 
                  top: -8, 
                  right: -8, 
                  backgroundColor: 'white',
                  border: '1px solid #999',
                  borderRadius: '50%',
                  width: 16,
                  height: 16,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: 10,
                  fontWeight: 'bold'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onAnnotationDeleted && annotation.id) {
                    onAnnotationDeleted(annotation.id);
                    setAnnotations(prev => prev.filter(a => a.id !== annotation.id));
                  }
                }}
              >
                ×
              </div>
            </div>
          );
          
        case 'highlight':
          return (
            <div 
              key={annotation.id}
              style={{ 
                position: 'absolute',
                left: annotation.x,
                top: annotation.y,
                width: annotation.width,
                height: annotation.height,
                backgroundColor: annotation.color || '#FFFF00',
                opacity: 0.3,
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
              onClick={() => {
                if (onAnnotationUpdated && annotation.id) {
                  onAnnotationUpdated(annotation);
                }
              }}
              onDoubleClick={() => {
                if (onAnnotationDeleted && annotation.id) {
                  onAnnotationDeleted(annotation.id);
                  setAnnotations(prev => prev.filter(a => a.id !== annotation.id));
                }
              }}
            />
          );
          
        case 'image':
          if (!annotation.content) return null;
          
          return (
            <div 
              key={annotation.id}
              style={{ 
                position: 'absolute',
                left: annotation.x,
                top: annotation.y,
                padding: 2,
                border: '1px solid #ccc',
                backgroundColor: 'white',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                zIndex: 5
              }}
            >
              <img 
                src={annotation.content as string} 
                alt="Annotation" 
                style={{ maxWidth: 200, maxHeight: 200 }} 
              />
              <div 
                style={{ 
                  position: 'absolute', 
                  top: -8, 
                  right: -8, 
                  backgroundColor: 'white',
                  border: '1px solid #999',
                  borderRadius: '50%',
                  width: 16,
                  height: 16,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: 10,
                  fontWeight: 'bold'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onAnnotationDeleted && annotation.id) {
                    onAnnotationDeleted(annotation.id);
                    setAnnotations(prev => prev.filter(a => a.id !== annotation.id));
                  }
                }}
              >
                ×
              </div>
            </div>
          );
          
        default:
          return null;
      }
    });
  };
  
  // Render current drawing path if applicable
  const renderCurrentPath = () => {
    if (!isDrawing || currentPath.length < 2) return null;
    
    if (editMode === 'draw') {
      return (
        <svg 
          style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0,
            width: viewport?.width || '100%',
            height: viewport?.height || '100%',
            pointerEvents: 'none'
          }}
        >
          <polyline
            points={currentPath.map(p => `${p.x},${p.y}`).join(' ')}
            fill="none"
            stroke="#FF0000"
            strokeWidth="2"
          />
        </svg>
      );
    } else if (editMode === 'highlight') {
      // For highlight, show a rectangle
      const startX = currentPath[0].x;
      const startY = currentPath[0].y;
      const endX = currentPath[currentPath.length - 1].x;
      const endY = currentPath[currentPath.length - 1].y;
      
      const x = Math.min(startX, endX);
      const y = Math.min(startY, endY);
      const width = Math.abs(endX - startX);
      const height = Math.abs(endY - startY);
      
      return (
        <div
          style={{
            position: 'absolute',
            left: x,
            top: y,
            width,
            height,
            backgroundColor: '#FFFF00',
            opacity: 0.3,
            pointerEvents: 'none'
          }}
        />
      );
    }
    
    return null;
  };

  if (loading && !renderedOnce) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress size={40} thickness={4} />
        <Typography mt={2}>Loading PDF...</Typography>
        {debugInfo && (
          <Typography variant="caption" color="text.secondary" mt={1}>
            Status: {debugInfo}
          </Typography>
        )}
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight={400} p={3}>
        <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
          Error loading PDF: {error}
        </Alert>
        {debugInfo && (
          <Typography variant="caption" color="text.secondary">
            Last operation: {debugInfo}
          </Typography>
        )}
        <Typography variant="body2" mt={2}>
          Please try uploading the document again or use a different PDF file.
        </Typography>
      </Box>
    );
  }

  return (
    <Box 
      ref={containerRef}
      display="flex" 
      flexDirection="column"
      justifyContent="center" 
      alignItems="center" 
      sx={{ 
        bgcolor: '#f5f5f5',
        p: 2,
        borderRadius: 1,
        minHeight: 400,
        width: '100%',
        overflow: 'auto'
      }}
    >
      {/* PDF Page Navigation */}
      {numPages > 0 && (
        <Box 
          sx={{ 
            display: 'flex',
            justifyContent: 'space-between', 
            alignItems: 'center',
            width: '100%',
            mb: 2,
            borderBottom: '1px solid #e0e0e0',
            pb: 1
          }}
        >
          <IconButton 
            onClick={onPrevPage} 
            disabled={currentPage <= 1 || pageRendering}
          >
            <NavigateBefore />
          </IconButton>
          
          <Typography>
            Page {currentPage} of {numPages}
          </Typography>
          
          <IconButton 
            onClick={onNextPage} 
            disabled={currentPage >= numPages || pageRendering}
          >
            <NavigateNext />
          </IconButton>
        </Box>
      )}
      
      {/* PDF Canvas with annotation layer */}
      <Box 
        sx={{ 
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          backgroundColor: 'white',
          boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
          p: 2
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => {
          if (isDrawing) {
            setIsDrawing(false);
            setCurrentPath([]);
          }
        }}
      >
        <canvas 
          ref={canvasRef} 
          style={{ 
            maxWidth: '100%', 
            height: 'auto'
          }} 
        />
        
        <div 
          ref={annotationLayerRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: editMode ? 'auto' : 'none'
          }}
        >
          {renderAnnotations()}
          {renderCurrentPath()}
        </div>
        
        {pageRendering && (
          <Box 
            sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.7)'
            }}
          >
            <CircularProgress size={30} />
          </Box>
        )}
      </Box>
      
      {debugInfo && (
        <Typography variant="caption" color="text.secondary" mt={1}>
          Status: {debugInfo}
        </Typography>
      )}
      
      {editMode && (
        <Typography variant="caption" color="primary" mt={1}>
          Editing Mode: {editMode.charAt(0).toUpperCase() + editMode.slice(1)}
        </Typography>
      )}
    </Box>
  );
};

export default PDFViewer; 